import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { tokens as c } from '../../theme';
import IconCircle from './IconCircle';

// Detail pages as the approved W2_Portal_BookingDetails board draws them
// (taken from views/BookingDetails.js): cards with an icon on a tinted
// circle and a title, then label / value rows. Lay cards out in a grid:
//   <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
//     <DetailCard icon={<PersonOutlineRoundedIcon />} hue="info" title={t('customer')}>
//       <InfoRow label={t('mobile')}>{formatPhoneDisplay(user.mobile)}</InfoRow>
//       <InfoRow label={t('email')}>{user.email}</InfoRow>
//     </DetailCard>
//   </Box>

/** One label / value row of a card (a <dt> and its <dd>). Empty values show a quiet dash. */
export function InfoRow({ label, children, alignTop = false, sx }) {
  const empty = children === null || children === undefined || children === '';
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(96px, 32%) minmax(0, 1fr)',
        columnGap: 1.5,
        alignItems: alignTop ? 'start' : 'center',
        py: 1.1,
        borderTop: `1px solid ${c.OUTLINE}`,
        '&:first-of-type': { borderTop: 0 },
        ...(sx || {}),
      }}
    >
      <Typography component="dt" sx={{ fontSize: 13.5, color: c.INK_2, lineHeight: 1.45 }}>
        {label}
      </Typography>
      <Box component="dd" sx={{ m: 0, minWidth: 0, fontSize: 14, color: empty ? c.TEXT_3 : c.INK, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
        {empty ? '—' : children}
      </Box>
    </Box>
  );
}

/** A card: an icon on a tinted circle, a title, an optional action, then InfoRows (or anything). */
export default function DetailCard({ icon, hue = 'accent', title, action, children, list = true, sx }) {
  return (
    <Paper component="section" aria-label={typeof title === 'string' ? title : undefined} sx={{ p: 2, minWidth: 0, ...(sx || {}) }}>
      {title || icon || action ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
          {icon ? <IconCircle icon={icon} hue={hue} size={34} /> : null}
          <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700, color: c.INK, flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
            {title}
          </Typography>
          {action || null}
        </Box>
      ) : null}
      {list ? (
        <Box component="dl" sx={{ m: 0 }}>
          {children}
        </Box>
      ) : (
        children
      )}
    </Paper>
  );
}
